import Pocketbase, {
  ClientResponseError,
  FileOptions,
  LocalAuthStore,
  RecordModel,
} from "pocketbase";
import { format } from "date-fns";

export interface UserModel extends RecordModel {
  username: string;
}

export interface BaseModel extends RecordModel {
  created: string;
  updated: string;
}

export interface MissionModel extends BaseModel {
  status?: "normal" | "disabled";
  title: string;
  platform: string;
  target: string;
  description: string;
  link: string;
  available_until: string;
  expired_at: string;
  cover: string;
}

export interface TaskModel extends BaseModel {
  mission: string;
  user: string;
  enroll_at: string;
  status?: "initial" | "uploaded" | "done";
}

export interface TaskResultModel extends BaseModel {
  task: string;
  account: string;
  user: string;
  mission: string;
  screenshot: string;
  audit_result?: "ok" | "reject" | "initial";
  audit_comment: string;
}

export interface AccountWithResult {
  account: ExternalAccountModel;
  result?: TaskResultModel;
}

export interface UserMissionDetail extends MissionModel {
  task?: TaskModel;
  results: AccountWithResult[];
}

export interface ExternalAccount {
  link: string;
  website: string;
  username: string;
  avatar: string;
}

export interface ExternalAccountModel extends BaseModel, ExternalAccount {}

export interface FileOwner {
  collectionName: string;
  id: string;
}

export interface TaskStatus
  extends TaskModel,
    Pick<MissionModel, "cover" | "title" | "available_until" | "expired_at"> {}

export interface HomeResponse {
  missions: MissionModel[];
  tasks: TaskStatus[];
}

export interface TaskList {
  tasks: TaskStatus[];
}

export interface AccountSummary {
  tasks: {
    initial: number;
    uploaded: number;
    ok: number;
    reject: number;
  };
  credits: {
    available: number;
    withdraw: number;
  };
}

export class APIClient extends Pocketbase {
  constructor() {
    super(import.meta.env.VITE_API_HOST, new LocalAuthStore());

    if(!this.authStore.isValid) {
      this.authStore.clear();
    }
  }

  get user() {
    return this.authStore.model as UserModel;
  }

  signout() {
    this.authStore.clear();
  }

  authWithCode(code: string) {
    const recordSerice = this.collection<UserModel>("users");

    return recordSerice.client
      .send(
        `/api/taskbooze/${recordSerice.collectionIdOrName}/auth-with-code`,
        {
          query: { code },
          method: "POST",
        }
      )
      .then((responseData) => {
        const record = recordSerice.decode(responseData?.record || {});

        recordSerice.client.authStore.save(responseData?.token, record);

        return Object.assign({}, responseData, {
          // normalize common fields
          token: responseData?.token || "",
          record: record as UserModel,
        });
      });
  }

  getFileUrl(
    record: FileOwner,
    filename: string,
    options?: FileOptions
  ): string {
    return this.files.getUrl(record, filename, options);
  }

  getHomeMissions() {
    return this.send<HomeResponse>("/api/taskbooze/user/home", {
      method: "GET",
    });
  }

  getMyTasks() {
    return this.send<TaskList>("/api/taskbooze/user/my-tasks", {
      method: "GET",
    });
  }

  withdrawCredits() {
    return this.send<TaskList>("/api/taskbooze/user/withdraw", {
      method: "POST",
    });
  }

  getAccountSummary() {
    return this.send<AccountSummary>("/api/taskbooze/user/account", {
      method: "GET",
    });
  }

  async getMissionDetail(id: string): Promise<UserMissionDetail> {
    const user = this.authStore.model;
    const mission = await this.collection<MissionModel>("missions").getOne(id);
    let task: TaskModel | undefined = undefined;
    const results: AccountWithResult[] = [];
    try {
      task = await this.collection<TaskModel>("tasks").getFirstListItem(
        `user = "${user?.id}" && mission = "${id}"`
      );

      const accounts = await this.collection<ExternalAccountModel>(
        "external_accounts"
      ).getFullList({
        filter: `user = "${user?.id}" && website="${mission?.platform}"`,
      });

      const taskResults = await this.collection<TaskResultModel>(
        "task_results"
      ).getFullList({
        filter: `mission = "${id}" && user ="${user?.id}"`,
        expand: "account",
      });

      for (const account of accounts) {
        results.push({
          account,
          result: taskResults.find((item) => item.account === account.id),
        });
      }
    } catch (e) {
      if (e instanceof ClientResponseError) {
        const err = e as ClientResponseError;
        // ignore 404 , not apply fot this mission yet
        if (err.status !== 404) {
          throw e;
        }
      } else {
        throw e;
      }
    }

    const data = {
      ...mission,
      task: task,
      results,
    };

    console.log(data);

    return data;
  }

  enrollMission(id: string) {
    const user = this.authStore.model;
    return this.collection<TaskModel>("tasks").create({
      mission: id,
      user: user?.id,
      enroll_at: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
    });
  }

  async getXHSProfile(url: string) {
    let fetchUrl = url;
    const pattern = "(https://www.xiaohongshu.com/user/profile/[\\S/]+)\\??.*";
    const match = url.match(pattern);
    if (match) {
      fetchUrl = match[1];
    } else {
      throw new Error("invalid url");
    }
    const formData = new FormData();
    formData.set("url", fetchUrl);
    const resp = await fetch(this.baseUrl + "/api/taskbooze/proxy-get", {
      method: "POST",
      body: formData,
    });

    const content = await resp.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const avatar = doc
      .querySelector('link[rel~="preload"][as~="image"]')
      ?.getAttribute("href");
    const finalUrl = doc
      .querySelector('meta[name~="og:url"]')
      ?.getAttribute("content");
    const username = doc
      .querySelector('meta[name~="og:title"]')
      ?.getAttribute("content")
      ?.replace(" - 小红书", "");
    if (!finalUrl || !finalUrl.match(pattern)) {
      throw new Error("page request failed");
    }

    if (!avatar) {
      throw new Error("avatar not found");
    }

    return { url: finalUrl, avatar, username: username || "" };
  }
}
